import React, { useEffect, useState, useCallback, useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import styles from "./institutionRevenueHistoryCard.module.css";
import {
  getInstitutionAverageTicketHistory,
  getInstitutionMonthlyAverageTicket,
} from "../../services/insights";
import { formatNumberToCurrency } from "../../utils/formatter";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const YEARS_REQUESTED = 5;

const MONTH_NAMES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

const InstitutionAverageTicketHistoryCard = ({ institutionId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selectedYear, setSelectedYear] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [monthlyError, setMonthlyError] = useState(null);

  const chartRef = useRef(null);

  const loadData = useCallback(async () => {
    if (!institutionId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await getInstitutionAverageTicketHistory({
        institutionId,
        years: YEARS_REQUESTED,
      });
      setData(result);
    } catch (err) {
      setError(err?.message || "Falha ao buscar histórico de ticket médio.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [institutionId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setSelectedYear(null);
    setMonthlyData(null);
    setMonthlyError(null);
  }, [institutionId]);

  const fetchMonthly = useCallback(
    async (year) => {
      if (!institutionId || !year) return;
      setMonthlyLoading(true);
      setMonthlyError(null);
      try {
        const result = await getInstitutionMonthlyAverageTicket({
          institutionId,
          year,
        });
        setMonthlyData(result);
      } catch (err) {
        setMonthlyError(err?.message || "Falha ao buscar ticket médio mensal.");
        setMonthlyData(null);
      } finally {
        setMonthlyLoading(false);
      }
    },
    [institutionId]
  );

  const handleYearSelect = useCallback(
    (year) => {
      if (selectedYear === year) {
        setSelectedYear(null);
        setMonthlyData(null);
        setMonthlyError(null);
        return;
      }
      setSelectedYear(year);
      fetchMonthly(year);
    },
    [selectedYear, fetchMonthly]
  );

  const handleCloseTable = useCallback(() => {
    setSelectedYear(null);
    setMonthlyData(null);
    setMonthlyError(null);
  }, []);

  if (!institutionId) return null;

  const history = data?.history || [];
  const hasData = history.length > 0 && history.some((h) => h.paidInstallments > 0);

  const chartData = {
    labels: history.map((item) => String(item.year)),
    datasets: [
      {
        label: "Ticket médio",
        data: history.map((item) => Number(item.averageTicket) || 0),
        backgroundColor: history.map((item) =>
          item.year === selectedYear ? "#F59E0B" : "#0962AD"
        ),
        borderColor: history.map((item) =>
          item.year === selectedYear ? "#F59E0B" : "#0962AD"
        ),
        borderWidth: 1,
        borderRadius: 4,
        maxBarThickness: 64,
        hoverBackgroundColor: "#F59E0B",
        hoverBorderColor: "#F59E0B",
      },
    ],
  };

  const handleChartClick = (event) => {
    const chart = chartRef.current;
    if (!chart) return;
    const elements = chart.getElementsAtEventForMode(
      event,
      "nearest",
      { intersect: true },
      false
    );
    if (!elements || elements.length === 0) return;
    const idx = elements[0].index;
    const yearItem = history[idx];
    if (yearItem && yearItem.paidInstallments > 0) {
      handleYearSelect(yearItem.year);
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    onHover: (event, chartElement) => {
      if (event?.native?.target) {
        event.native.target.style.cursor =
          chartElement.length > 0 ? "pointer" : "default";
      }
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#0962AD",
        titleColor: "#ffffff",
        bodyColor: "#ffffff",
        borderColor: "#F59E0B",
        borderWidth: 1,
        callbacks: {
          label: (context) => {
            const item = history[context.dataIndex];
            const lines = [
              `Ticket médio: ${formatNumberToCurrency(context.raw)}`,
            ];
            if (item) {
              lines.push(
                `Parcelas pagas: ${item.paidInstallments.toLocaleString("pt-BR")}`
              );
              lines.push(
                `Receita: ${formatNumberToCurrency(item.totalReceived)}`
              );
            }
            return lines;
          },
          afterBody: () => "Clique para ver mês a mês",
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: "#0962AD", font: { size: 13, weight: "700" } },
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: "#293050",
          font: { size: 11 },
          callback: (value) => formatNumberToCurrency(value),
        },
        grid: { color: "rgba(9, 98, 173, 0.08)" },
      },
    },
  };

  const subtitle = hasData
    ? `Período: ${data.startYear} – ${data.endYear} · Mensalidades`
    : "Sem dados de ticket médio disponíveis";

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.titleGroup}>
          <h3 className={styles.title}>Ticket médio dos últimos {YEARS_REQUESTED} anos</h3>
          <p className={styles.subtitle}>{subtitle}</p>
        </div>
        {hasData && (
          <div className={styles.totalGroup}>
            <span className={styles.totalLabel}>Ticket médio no período</span>
            <span className={styles.totalValue}>
              {formatNumberToCurrency(data.periodAverageTicket)}
            </span>
          </div>
        )}
      </div>

      {data?.hasInsufficientData && hasData && (
        <div className={styles.warning}>
          <span className={styles.warningIcon}>i</span>
          <span>
            A instituição ainda não possui {YEARS_REQUESTED} anos de histórico.
            Mostrando do ano mais antigo ({data.oldestAvailableYear}) até o mais
            recente ({data.mostRecentAvailableYear}).
          </span>
        </div>
      )}

      {loading && <div className={styles.loading}>Carregando histórico de ticket médio...</div>}
      {error && !loading && <div className={styles.error}>{error}</div>}

      {!loading && !error && !hasData && (
        <div className={styles.empty}>
          Ainda não há mensalidades pagas registradas para esta instituição.
        </div>
      )}

      {!loading && !error && hasData && (
        <>
          <div className={styles.chartWrapper}>
            <Bar
              ref={chartRef}
              data={chartData}
              options={chartOptions}
              onClick={handleChartClick}
            />
          </div>

          {selectedYear && (
            <div className={styles.monthlyTableWrapper}>
              <div className={styles.monthlyHeader}>
                <div className={styles.monthlyTitleGroup}>
                  <h4 className={styles.monthlyTitle}>
                    Ticket médio mês a mês — {selectedYear}
                  </h4>
                  {monthlyData && (
                    <p className={styles.monthlySubtitle}>
                      Ticket médio do ano:{" "}
                      <strong>
                        {formatNumberToCurrency(monthlyData.yearAverageTicket)}
                      </strong>
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  className={styles.closeBtn}
                  onClick={handleCloseTable}
                  aria-label="Fechar tabela"
                  title="Fechar"
                >
                  ×
                </button>
              </div>

              {monthlyLoading && (
                <div className={styles.loading}>Carregando ticket médio mensal...</div>
              )}
              {monthlyError && !monthlyLoading && (
                <div className={styles.error}>{monthlyError}</div>
              )}

              {!monthlyLoading && !monthlyError && monthlyData && (
                <div className={styles.tableWrapper}>
                  <table className={styles.monthlyTable}>
                    <thead>
                      <tr>
                        <th>Mês</th>
                        <th className={styles.numericCell}>Parcelas pagas</th>
                        <th className={styles.numericCell}>Receita</th>
                        <th className={styles.numericCell}>Ticket médio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyData.months.map((m) => (
                        <tr key={m.month}>
                          <td>{MONTH_NAMES[m.month - 1]}</td>
                          <td className={styles.numericCell}>
                            {m.paidInstallments.toLocaleString("pt-BR")}
                          </td>
                          <td className={styles.numericCell}>
                            {formatNumberToCurrency(m.totalReceived)}
                          </td>
                          <td className={styles.numericCell}>
                            {formatNumberToCurrency(m.averageTicket)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td>Ano</td>
                        <td className={styles.numericCell}>
                          {monthlyData.yearPaidInstallments.toLocaleString("pt-BR")}
                        </td>
                        <td className={styles.numericCell}>
                          {formatNumberToCurrency(monthlyData.yearTotalReceived)}
                        </td>
                        <td className={styles.numericCell}>
                          {formatNumberToCurrency(monthlyData.yearAverageTicket)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default InstitutionAverageTicketHistoryCard;
